import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Ticket, ShoppingBag, History, Gift, ChevronRight, 
  Tag, Calendar, ArrowRight, Package, Sparkles,
  TrendingUp, CheckCircle, Clock
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const TIER_COLORS = {
  'SILVER': 'from-gray-400 to-gray-600',
  'GOLD': 'from-yellow-400 to-yellow-600',
  'DIAMOND': 'from-cyan-400 to-blue-500',
  'VIP': 'from-purple-400 to-purple-600',
  'PREMIUM': 'from-rose-400 to-red-600'
};

const TIER_ICONS = {
  'SILVER': '🥈',
  'GOLD': '🥇',
  'DIAMOND': '💎',
  'VIP': '👑',
  'PREMIUM': '⭐'
};

const PassInventory = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('inventory');

  useEffect(() => {
    fetchPassData();
  }, []);

  const fetchPassData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/p2p/my-passes');
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch pass data:', error);
      toast.error('Failed to load pass inventory');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="pass-inventory-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/20 via-background to-accent/10 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-primary/20 rounded-2xl flex items-center justify-center">
              <Ticket className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">My Pass Inventory</h1>
              <p className="text-muted-foreground">Manage your listing passes</p>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass-card p-4 text-center">
              <Package className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold">{data?.summary?.total_passes || 0}</p>
              <p className="text-xs text-muted-foreground">Available Passes</p>
            </div>
            <div className="glass-card p-4 text-center">
              <ShoppingBag className="w-6 h-6 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold">{data?.summary?.total_purchased || 0}</p>
              <p className="text-xs text-muted-foreground">Purchased</p>
            </div>
            <div className="glass-card p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{data?.summary?.total_used || 0}</p>
              <p className="text-xs text-muted-foreground">Used</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Gift className="w-6 h-6 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold">{data?.summary?.total_redeemed || 0}</p>
              <p className="text-xs text-muted-foreground">From Coupons</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
          {[
            { id: 'inventory', label: 'My Passes', icon: Package },
            { id: 'purchases', label: 'Purchase History', icon: ShoppingBag },
            { id: 'usage', label: 'Usage History', icon: History },
            { id: 'redemptions', label: 'Coupon Redemptions', icon: Gift }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === tab.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'hover:bg-white/10'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <div className="space-y-4">
            {data?.inventory?.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <Ticket className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No Passes Yet</h3>
                <p className="text-muted-foreground mb-6">
                  Purchase listing passes to start selling on the marketplace
                </p>
                <Link
                  to="/deals/buy-pass"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold"
                >
                  Buy Passes <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data?.inventory?.map((pass, idx) => (
                  <div 
                    key={idx} 
                    className="glass-card overflow-hidden"
                    data-testid={`pass-card-${pass.tier_name}`}
                  >
                    <div className={`p-4 bg-gradient-to-r ${TIER_COLORS[pass.tier_name] || 'from-gray-500 to-gray-700'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{TIER_ICONS[pass.tier_name] || '🎫'}</span>
                          <span className="font-bold text-white text-lg">{pass.tier_name}</span>
                        </div>
                        <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                          <span className="font-bold text-white">{pass.quantity}</span>
                          <span className="text-white/70 text-sm ml-1">passes</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-muted-foreground text-sm">Value Limit</span>
                        <span className="font-semibold">₹{pass.value_limit?.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-muted-foreground text-sm">Pass Price</span>
                        <span className="font-semibold">₹{pass.price?.toLocaleString()}</span>
                      </div>
                      {pass.features && (
                        <div className="flex flex-wrap gap-2">
                          {pass.features.max_images && (
                            <span className="px-2 py-1 bg-white/10 rounded text-xs">
                              {pass.features.max_images} images
                            </span>
                          )}
                          {pass.features.allow_video && (
                            <span className="px-2 py-1 bg-white/10 rounded text-xs">
                              Video
                            </span>
                          )}
                          {pass.features.allow_timer && (
                            <span className="px-2 py-1 bg-white/10 rounded text-xs">
                              Timer
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Quick Action */}
            <div className="mt-6">
              <Link
                to="/deals/buy-pass"
                className="glass-card p-4 flex items-center justify-between hover:border-primary transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Need More Passes?</p>
                    <p className="text-sm text-muted-foreground">Buy passes to create more listings</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </Link>
            </div>
          </div>
        )}

        {/* Purchase History Tab */}
        {activeTab === 'purchases' && (
          <div className="space-y-3">
            {data?.purchase_history?.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No purchases yet</p>
              </div>
            ) : (
              data?.purchase_history?.map((item, idx) => (
                <div key={idx} className="glass-card p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-r ${TIER_COLORS[item.tier_name] || 'from-gray-500 to-gray-700'}`}>
                      <span className="text-xl">{TIER_ICONS[item.tier_name] || '🎫'}</span>
                    </div>
                    <div>
                      <p className="font-semibold">{item.tier_name} Pass × {item.quantity}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(item.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-500">-₹{item.amount?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{item.payment_method}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Usage History Tab */}
        {activeTab === 'usage' && (
          <div className="space-y-3">
            {data?.usage_history?.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <History className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No passes used yet</p>
              </div>
            ) : (
              data?.usage_history?.map((item, idx) => (
                <div key={idx} className="glass-card p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                      <Tag className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.tier_name} Pass Used</p>
                      <p className="text-sm text-muted-foreground">
                        Listing: {item.listing_title || item.listing_id}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(item.created_at)}
                      </p>
                    </div>
                  </div>
                  <Link
                    to={`/deals/${item.listing_id}`}
                    className="px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20"
                  >
                    View Listing
                  </Link>
                </div>
              ))
            )}
          </div>
        )}

        {/* Redemptions Tab */}
        {activeTab === 'redemptions' && (
          <div className="space-y-3">
            {data?.redemption_history?.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <Gift className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No coupon redemptions</p>
              </div>
            ) : (
              data?.redemption_history?.map((item, idx) => (
                <div key={idx} className="glass-card p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                      <Gift className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <p className="font-semibold">{item.tier_name} Pass × {item.quantity}</p>
                      <p className="text-sm text-muted-foreground">
                        Code: <span className="font-mono">{item.coupon_code}</span>
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(item.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="px-3 py-1 bg-purple-500/20 text-purple-500 rounded-full text-sm font-semibold">
                    FREE
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PassInventory;
